/**
 * V2 Signup — dark "standard" public /signup page.
 *
 * Behavior mirrors the classic SignupPage (Firebase email + Google signup,
 * alpha-agreement gate, email-verification "sent" state) with the demo-feedback
 * fixes (B-258):
 *  - Google sign-in is FIRST (how most people sign up); email/password below.
 *  - The agreement checkbox no longer disables the buttons — clicking either
 *    without it checked prompts for it instead (other sites don't gate this
 *    way and users couldn't tell why the button was dead).
 *  - A `?pack=<id>` purchase intent is stashed for post-auth checkout.
 *  - PRP-MCP-10: an optional "Referral code" field, prefilled from `?ref=` or the
 *    `aiea_ref` cookie; on submit it is stored in the cookie, which `/me/bootstrap`
 *    sends (the backend binds only an active affiliate, only for a new account).
 *
 * The dark token wrapper lives in {@link AuthShell} (module scope — an inline
 * shell component used to remount the form on every keystroke, eating all but
 * the first typed character).
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { authErrorMessage, logAuthError } from '../../lib/authErrors';
import {
  POPUP_RETRY_PROMPT,
  completeRedirectSignIn,
  isPopupRetry,
  startGoogleSignIn,
  withPopupRetry,
} from '../../lib/googleSignIn';
import { stashPack } from '../../lib/pendingCheckout';
import { nextFromSearch, readReturnPath, stashReturnPath } from '../../lib/postLoginReturn';
import {
  captureReferralFromUrl,
  clearReferralCookie,
  readReferralCookie,
  setReferralCookie,
} from '../../lib/referral';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AuthShell } from '../components/AuthShell';
import { AlphaAgreementText } from '../../content/alphaAgreement';

/** Public signup screen, V2 dark styling. */
export default function V2SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referral, setReferral] = useState(
    () => captureReferralFromUrl(location.search) ?? readReferralCookie() ?? '',
  );

  const popupRetry = isPopupRetry(location.search);

  // PRP-MCP-11 G4: finish a Google redirect that returned to /signup (the
  // agreement was checked and the referral cookie kept before it left).
  useEffect(() => {
    let cancelled = false;
    // Read before the user is set: ProtectedRoute clears the stored path on landing.
    const target = readReturnPath(location.search) ?? '/';
    void completeRedirectSignIn(auth, location.search).then((outcome) => {
      if (cancelled) return;
      if (outcome.kind === 'signed-in') navigate(target, { replace: true });
      else if (outcome.kind === 'retry-popup') {
        navigate({ pathname: '/signup', search: withPopupRetry(location.search) }, { replace: true });
      } else if (outcome.kind === 'error') setError(outcome.message);
    });
    return () => {
      cancelled = true;
    };
    // Runs once per page load: a redirect result is consumed by the first call.
  }, []);

  // Stash a marketing-link purchase intent; the dashboard consumes it after
  // auth (it survives the email-verification round trip via localStorage).
  useEffect(() => {
    const pack = searchParams.get('pack');
    if (pack) stashPack(pack);
  }, [searchParams]);

  /** Keep the (possibly edited) referral code for /me/bootstrap; a cleared field drops it. */
  function keepReferral() {
    if (referral.trim()) setReferralCookie(referral);
    else clearReferralCookie();
  }

  /** Common gate: both signup paths require the agreement — but we PROMPT
   *  instead of disabling the buttons. */
  function requireAgreement(): boolean {
    keepReferral();
    if (agreed) return true;
    setError('Please check the box to accept the Alpha Agreement, then continue.');
    setShowAgreement(true);
    return false;
  }

  async function handleEmailSignup(e: React.FormEvent) {
    e.preventDefault();
    if (!requireAgreement()) return;
    if (!email || !password) {
      setError('Enter an email and password to create your account.');
      return;
    }
    setBusy(true);
    setError(null);
    const target = readReturnPath(location.search) ?? '/';
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      // Fire a verification email for later account recovery, but DON'T gate on
      // it: the free signup credit is granted on bootstrap regardless (B-368),
      // and the old "check your email, then sign in" wall stranded users before
      // they ever reached the app — Firebase's verification mail often lands in
      // spam, so most never clicked it and left with zero credits.
      // createUserWithEmailAndPassword already signed them in, so drop them
      // straight into the app (same as the Google path) where
      // useCredits → /me/bootstrap grants the credit.
      void sendEmailVerification(cred.user).catch(() => {});
      navigate(target);
    } catch (err) {
      logAuthError(err, 'email signup');
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    if (!requireAgreement()) return;
    setBusy(true);
    setError(null);
    const target = readReturnPath(location.search) ?? '/';
    const next = nextFromSearch(location.search);
    if (next) stashReturnPath(next); // the handler's popup fallback has no ?next=
    try {
      // Google accounts are pre-verified. Redirect: the page leaves and the
      // effect above finishes it on return. Retry page: popup, then navigate.
      await startGoogleSignIn(auth, { popup: popupRetry });
      if (popupRetry) navigate(target);
    } catch (err) {
      logAuthError(err, popupRetry ? 'google popup' : 'google redirect');
      setError(authErrorMessage(err));
    } finally {
      setBusy(false); // never reached on a successful redirect (the page has left)
    }
  }

  return (
    <AuthShell>
      <div className="mb-5">
        <h1 className="text-[19px] font-semibold text-[var(--tx-hi)]">
          Try the PDF &rarr; ESX converter
        </h1>
        <p className="mt-1.5 text-[13px] text-[var(--tx-mid)]">
          Create a free account &mdash; your first 3 conversions are on us.
        </p>
      </div>

      {/* Agreement — applies to BOTH signup paths, so it sits above them. */}
      <label className="mb-4 flex items-start gap-2 text-[12.5px] text-[var(--tx-mid)]">
        <input
          type="checkbox"
          className="mt-0.5 accent-[var(--accent)]"
          checked={agreed}
          onChange={(e) => {
            setAgreed(e.target.checked);
            if (e.target.checked) setError(null);
          }}
          aria-label="I agree to the Alpha Agreement"
        />
        <span>
          I have read and agree to the{' '}
          <button
            type="button"
            className="text-[var(--accent-ink)] underline"
            onClick={() => setShowAgreement((s) => !s)}
          >
            Alpha Agreement
          </button>
          .
        </span>
      </label>
      {showAgreement && (
        <div className="mb-4 max-h-56 overflow-y-auto rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-inset)] p-3 text-[12.5px] text-[var(--tx-mid)]">
          <AlphaAgreementText />
        </div>
      )}
      {error && (
        <p role="alert" className="mb-3 text-[12.5px] font-medium text-[var(--bad)]">
          {error}
        </p>
      )}
      {popupRetry && !error && (
        <p className="mb-3 text-[12.5px] font-medium text-[var(--tx-hi)]">{POPUP_RETRY_PROMPT}</p>
      )}

      {/* PRP-MCP-10: applies to both signup paths, so it sits above them. */}
      <div className="mb-4 grid gap-1.5">
        <label htmlFor="referral" className="text-[12px] font-medium text-[var(--tx-mid)]">
          Referral code <span className="text-[var(--tx-faint)]">(optional)</span>
        </label>
        <Input
          id="referral"
          value={referral}
          onChange={(e) => setReferral(e.target.value)}
          onBlur={keepReferral}
          autoComplete="off"
          maxLength={12}
        />
      </div>

      {/* Google first — it's how most people sign up. */}
      <Button
        variant="primary"
        className="w-full justify-center"
        onClick={handleGoogle}
        disabled={busy}
      >
        Continue with Google
      </Button>

      <div className="my-4 flex items-center gap-3 text-[11px] text-[var(--tx-faint)]">
        <span className="h-px flex-1 bg-[var(--bd-subtle)]" />
        or sign up with email
        <span className="h-px flex-1 bg-[var(--bd-subtle)]" />
      </div>

      <form onSubmit={handleEmailSignup} className="grid gap-4">
        <div className="grid gap-1.5">
          <label htmlFor="email" className="text-[12px] font-medium text-[var(--tx-mid)]">
            Email
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="password" className="text-[12px] font-medium text-[var(--tx-mid)]">
            Password
          </label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <Button
          type="submit"
          variant="secondary"
          className="w-full justify-center"
          disabled={busy}
        >
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <p className="mt-4 text-center text-[13px] text-[var(--tx-mid)]">
        Already have an account?{' '}
        <Link
          to={{ pathname: '/login', search: location.search }}
          className="text-[var(--accent-ink)] underline"
        >
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
